import {useCallback, useRef, type RefObject} from 'react'

import type {HeroScreenId} from './HeroCarousel.data'
import {eases} from '../../utils/animation'

interface MotionRef {
  visual: RefObject<HTMLDivElement | null>
  isDesktopView: boolean
}

const useMotion = ({visual, isDesktopView}: MotionRef) => {
  const currentAnimations = useRef<Animation[]>([])

  const reset = useCallback(() => {
    if (!visual.current) return []

    if (currentAnimations.current.length) {
      for (const animation of currentAnimations.current) {
        animation.cancel()
      }
    }

    currentAnimations.current = []

    const visuals: HTMLElement[] = Array.from(visual.current.querySelectorAll('img, video'))
    return visuals
  }, [visual])

  const showPlan = useCallback(() => {
    const visuals = reset()
    if (!visuals.every(Boolean)) return

    visuals[0]!.style.opacity = '1'
    visuals[1]!.style.opacity = '0'
    visuals[2]!.style.opacity = '0'

    const inProps = {opacity: 1, translate: '0 0'}
    const outProps = {opacity: 0, translate: '0 0'}

    const step2In = visuals[1]!.animate([outProps, inProps], {
      delay: 1600,
      fill: 'forwards',
      duration: 400,
      easing: eases.easeInOutCirc,
    })
    const step1Out = visuals[0]!.animate([inProps, outProps], {
      delay: 2000,
      fill: 'forwards',
      duration: 400,
    })
    currentAnimations.current.push(step2In)
    currentAnimations.current.push(step1Out)

    const step3In = visuals[2]!.animate([outProps, inProps], {
      delay: 3200,
      fill: 'forwards',
      duration: 400,
      easing: eases.easeInOutCirc,
    })
    const step2Out = visuals[1]!.animate([inProps, outProps], {
      delay: 3600,
      fill: 'forwards',
      duration: 400,
      easing: eases.easeInOutCirc,
    })

    currentAnimations.current.push(step3In)
    currentAnimations.current.push(step2Out)
  }, [reset])

  const showCollaborate = useCallback(() => {
    const visuals = reset()
    if (!visuals.every(Boolean)) return

    visuals[0]!.style.opacity = '1'
    visuals[1]!.style.opacity = '0'

    if (!isDesktopView) return

    const step2In = visuals[1]!.animate(
      [
        {
          opacity: 0,
          translate: '20px 0',
        },
        {
          opacity: 1,
          translate: '0 0',
        },
      ],
      {delay: 600, fill: 'forwards', duration: 500, easing: eases.easeInOutCirc},
    )
    currentAnimations.current.push(step2In)
  }, [isDesktopView, reset])

  const showAutomate = useCallback(() => {
    const visuals = reset()
    if (!visuals.every(Boolean)) return

    visuals[0]!.style.opacity = '1'
    visuals[1]!.style.opacity = '0'
    visuals[2]!.style.opacity = '0'

    const translateString = isDesktopView ? '20px 0' : '0 20px'

    const step2In = visuals[1]!.animate(
      [
        {
          opacity: 0,
          translate: translateString,
        },
        {
          opacity: 1,
          translate: '0 0',
        },
      ],
      {delay: 600, fill: 'forwards', duration: 500, easing: eases.easeInOutCirc},
    )
    currentAnimations.current.push(step2In)

    const step3In = visuals[2]!.animate(
      [
        {
          opacity: 0,
          translate: translateString,
        },
        {
          opacity: 1,
          translate: '0 0',
        },
      ],
      {delay: 1200, fill: 'forwards', duration: 500, easing: eases.easeInOutCirc},
    )
    currentAnimations.current.push(step3In)
  }, [isDesktopView, reset])

  const showSecure = useCallback(() => {
    const visuals = reset()
    if (!visuals.every(Boolean)) return

    visuals[0]!.style.opacity = '1'
    visuals[1]!.style.opacity = '0'

    if (!isDesktopView) return

    const step2In = visuals[1]!.animate(
      [
        {
          opacity: 0,
          translate: '20px 0',
        },
        {
          opacity: 1,
          translate: '0 0',
        },
      ],
      {delay: 800, fill: 'forwards', duration: 400, easing: eases.easeInOutCirc},
    )
    currentAnimations.current.push(step2In)
  }, [isDesktopView, reset])

  const show = useCallback(
    (screenId: HeroScreenId) => {
      switch (screenId) {
        case 'plan':
          showPlan()
          break
        case 'collaborate':
          showCollaborate()
          break
        case 'automate':
          showAutomate()
          break
        case 'secure':
          showSecure()
          break
      }
    },
    [showAutomate, showCollaborate, showPlan, showSecure],
  )

  const pause = useCallback(() => {
    if (currentAnimations.current.length) {
      for (const animation of currentAnimations.current) {
        if (animation.playState === 'running') {
          animation.pause()
        }
      }
    }
  }, [])

  const resume = useCallback(() => {
    if (currentAnimations.current.length) {
      for (const animation of currentAnimations.current) {
        if (animation.playState === 'paused') {
          animation.play()
        }
      }
    }
  }, [])

  return {show, pause, resume}
}

export default useMotion
